import { makeAutoObservable, runInAction } from "mobx";
import type {
  SourceNode,
  SourceFolder,
  SourceFile,
} from "../../features/tars/notebook-details/sidebar/sources-types";
import {
  isFolder,
  normalizeSourceType,
} from "../../features/tars/notebook-details/sidebar/sources-types";
import type { SourceListItem } from "../../features/tars/tars-hooks/useSourceList";
import type { UploadSourceResponse } from "../../features/tars/tars-hooks/useUploadSource";
import { buildStateFromList, buildNodesForFolder, generateId } from "./tars-sources-helpers";
import { dataBankDisplayFieldsFromMore } from "../../features/tars/notebook-details/sidebar/add-source/connectDataBankNodeFields";

export interface TarsNotebook {
  id: number;
  name: string;
  [key: string]: unknown;
}

export interface ServerFolderResponse {
  id: number;
  name: string;
  parent_id: number | null;
  type: string;
}

export type CanvasType = "thinkSpace" | "analytics" | null;

export interface SelectedCanvas {
  id: number;
  name: string;
}

export class TarsStore {
  rootStore: any;
  selectedNotebook: TarsNotebook | null = null;
  selectedCanvasType: CanvasType = null;
  /** Currently selected canvas (from canvas list); used for header title. */
  selectedCanvas: SelectedCanvas | null = null;
  nodes: Record<string, SourceNode> = {};
  rootIds: string[] = [];

  constructor(rootStore: any) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  setSelectedNotebook(notebook: TarsNotebook | null) {
    this.selectedNotebook = notebook;
  }

  setSelectedCanvasType(type: CanvasType) {
    this.selectedCanvasType = type;
  }

  setSelectedCanvas(canvas: SelectedCanvas | null) {
    this.selectedCanvas = canvas;
  }

  clearSelectedNotebook() {
    this.selectedNotebook = null;
  }

  setSourcesFromList(sources: SourceListItem[]) {
    const { nodes, rootIds } = buildStateFromList(sources);
    this.nodes = nodes;
    this.rootIds = rootIds;
  }

  setFolderChildren(folderId: string, sources: SourceListItem[]) {
    const { nodes: newNodes, childIds } = buildNodesForFolder(sources, folderId);
    Object.assign(this.nodes, newNodes);
    const folder = this.nodes[folderId];
    if (folder && isFolder(folder)) {
      this.nodes[folderId] = { ...folder, childrenIds: childIds };
    }
  }

  addFolder(parentId: string | null, name: string): SourceFolder {
    const id = generateId("folder");
    const folder: SourceFolder = { id, name, parentId, type: "folder", childrenIds: [] };
    this.nodes[id] = folder;
    if (parentId) {
      const parent = this.nodes[parentId];
      if (parent && isFolder(parent)) parent.childrenIds.push(id);
    } else this.rootIds.push(id);
    return folder;
  }

  addFolderFromServer(server: ServerFolderResponse, localParentId?: string | null): SourceFolder {
    const id = String(server.id);
    const parentId = localParentId !== undefined
      ? localParentId
      : server.parent_id != null ? String(server.parent_id) : null;
    const folder: SourceFolder = { id, name: server.name, parentId, type: "folder", childrenIds: [] };
    this.nodes[id] = folder;
    if (parentId) {
      const parent = this.nodes[parentId];
      if (parent && isFolder(parent)) {
        this.nodes[parentId] = {
          ...parent,
          childrenIds: [...parent.childrenIds, id],
        };
      }
    } else {
      this.rootIds = [...this.rootIds, id];
    }
    return folder;
  }

  addSourceFromServer(
    server: UploadSourceResponse,
    localParentId?: string | null,
    /** Client `more` from the upload request; merged when the API omits fields (e.g. es_index). */
    requestMore?: Record<string, unknown>
  ): SourceNode {
    return runInAction(() => {
      if (server.type === "folder") {
        return this.addFolderFromServer(server as ServerFolderResponse, localParentId);
      }
      const id = String(server.id);
      const parentId = localParentId !== undefined
        ? localParentId
        : server.parent_id != null ? String(server.parent_id) : null;
      const more = {
        ...(requestMore ?? {}),
        ...((server.more as Record<string, unknown>) ?? {}),
      };
      const ext = server.extension ?? more.extension;
      const s3Key = (server as UploadSourceResponse & { s3_key?: string }).s3_key;
      const displayName =
        server.name?.trim() ||
        (s3Key ? s3Key.split("/").pop() : null) ||
        (ext != null ? String(ext) : "file");
      const file: SourceFile = {
        id,
        name: displayName,
        parentId,
        type: normalizeSourceType(String(server.type ?? "file")),
        ...(ext != null && { extension: String(ext) }),
        ...(more.url != null && { url: String(more.url) }),
        ...(s3Key != null && s3Key !== "" && { s3_key: s3Key }),
        ...(typeof more.es_index === "string" && more.es_index.trim() !== ""
          ? { es_index: more.es_index.trim() }
          : {}),
        ...dataBankDisplayFieldsFromMore(more),
      };
      this.nodes[id] = file;
      if (parentId) {
        const parent = this.nodes[parentId];
        if (parent && isFolder(parent)) {
          this.nodes[parentId] = {
            ...parent,
            childrenIds: [...parent.childrenIds, id],
          };
        }
      } else {
        this.rootIds = [...this.rootIds, id];
      }
      return file;
    });
  }

  addFile(parentId: string | null, name: string, extension?: string): SourceFile {
    const id = generateId("file");
    const ext = extension ?? name.split(".").pop() ?? "file";
    const file: SourceFile = { id, name, parentId, type: "file", extension: ext };
    this.nodes[id] = file;
    if (parentId) {
      const parent = this.nodes[parentId];
      if (parent && isFolder(parent)) parent.childrenIds.push(id);
    } else this.rootIds.push(id);
    return file;
  }

  addStubNode(
    parentId: string | null,
    type: SourceFile["type"],
    defaultName: string,
    extra?: { url?: string }
  ): SourceFile {
    const id = generateId(type);
    const file: SourceFile = {
      id,
      name: defaultName,
      parentId,
      type,
      ...(type === "link" && extra?.url != null && { url: extra.url }),
    };
    this.nodes[id] = file;
    if (parentId) {
      const parent = this.nodes[parentId];
      if (parent && isFolder(parent)) parent.childrenIds.push(id);
    } else this.rootIds.push(id);
    return file;
  }

  rename(id: string, name: string) {
    const node = this.nodes[id];
    if (node) node.name = name;
  }

  updateNode(id: string, updates: { name?: string; url?: string }) {
    const node = this.nodes[id];
    if (!node) return;
    if (updates.name !== undefined) node.name = updates.name;
    if (node.type === "link" && updates.url !== undefined) (node as SourceFile).url = updates.url;
  }

  getNode(id: string): SourceNode | undefined {
    return this.nodes[id];
  }

  getFolderPath(parentId: string | null): string {
    if (parentId == null || parentId === "") return "";
    const segments: string[] = [];
    let currentId: string | null = parentId;
    while (currentId) {
      const node = this.nodes[currentId];
      if (!node) break;
      segments.push(node.name);
      currentId = node.parentId;
    }
    segments.reverse();
    return segments.join("/");
  }

  getChildren(parentId: string | null): SourceNode[] {
    const ids = parentId
      ? (this.nodes[parentId] as SourceFolder)?.childrenIds ?? []
      : this.rootIds;
    return ids.map((id) => this.nodes[id]).filter(Boolean) as SourceNode[];
  }

  private collectDescendantIds(id: string): string[] {
    const node = this.nodes[id];
    if (!node) return [];
    let ids = [id];
    if (isFolder(node)) {
      for (const childId of node.childrenIds) ids = ids.concat(this.collectDescendantIds(childId));
    }
    return ids;
  }

  remove(id: string) {
    return runInAction(() => {
      const node = this.nodes[id];
      const parentId = node?.parentId ?? null;
      for (const rid of this.collectDescendantIds(id)) delete this.nodes[rid];
      if (parentId) {
        const parent = this.nodes[parentId];
        if (parent && isFolder(parent)) {
          this.nodes[parentId] = {
            ...parent,
            childrenIds: parent.childrenIds.filter((cid) => cid !== id),
          };
        }
      } else {
        this.rootIds = this.rootIds.filter((rid) => rid !== id);
      }
    });
  }
}
